import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import MaterialReactTable from 'material-react-table';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import { Form, Container, Row, Col } from "react-bootstrap";
import Axios from '../../lib/axios/axiosInstance';
import { Slide, toast } from 'react-toastify';
import { Delete, Print, Download, List, Edit } from '@mui/icons-material';
import Modal from 'react-bootstrap/Modal';
import { useNavigate, Link, useParams, useSearchParams } from "react-router-dom";
import * as AuthVerify from '../../lib/common';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

const CustomTable = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [dmShow, setDmShow] = useState(false);
  const selectedMatnum = useRef(false);

  const [editPermission, setEditPermission] = useState(false);

  const [columnFilters, setColumnFilters] = useState([]);
  const [columnFilterFns, setColumnFilterFns] = useState({
    budgetdesc: 'contains',
    budgettype: 'contains',
    limitscheme: 'contains',
    ccname: 'contains',
    effdate: 'lessThan',
    enddate: 'lessThan'
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const tableInstanceRef = useRef(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5
  });

  useEffect(async () => {
    setEditPermission(AuthVerify.getPermissions("Budget")?.edit);
  }, [searchParams])

  const handleDeleteRow = useCallback(
    (row) => {
      setDmShow(false);
      if (!row) row = selectedMatnum.current;
      if (!row) return;
      deleteHandler();
    },
    [data],
  );

  const findFilterCondition = (name) => {
    var condition = '';
    if (columnFilterFns[name]) {
      condition = columnFilterFns[name];
    }
    // if(condition==''){
    //   condition = initialColumnFiltersFns[name];
    // }
    return condition;
  }

  const fetchDataFromAPI = async (reset = false) => {
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }

    let filterQuery = '';
    let sorterQuery = '';

    if (!reset) {
      if (columnFilters.length > 0) {
        columnFilters.forEach(element => {
          filterQuery += `&filter=${element.id},${findFilterCondition(element.id)},${element.value}`;
        });
      }
      if (sorting.length > 0) {
        sorting.forEach(element => {
          sorterQuery += `&sortby=${element.id}&orderby=${element.desc ? 'desc' : 'asc'}`;
        });
      }
    } else {
      setPagination({
        pageIndex: 0,
        pageSize: 5
      });
      tableInstanceRef.current.resetRowSelection();
    }

    const user = AuthVerify.getUser();
    let url = `/corporate/budget?limit=${pagination.pageSize}&offset=${pagination.pageIndex * pagination.pageSize}${sorterQuery}${filterQuery}`;
    await Axios.get(url)
      .then(function (response) {
        setIsError(false);
        setIsLoading(false);
        setIsRefetching(false);
        if (response && response.data?.data) {
          setData(response.data?.data.budgets.items ?? []);
          setRowCount(response.data?.data.budgets.count);
        }
        else {
          toast.error(response.statusText ?? "Data fetching failed !", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 3000,
            transition: Slide,
          });
        }
      })
      .catch(function (error) {
        // navigate('/');
      });
  };


  useEffect(() => {
    fetchDataFromAPI();
  }, [
    columnFilters,
    globalFilter,
    pagination.pageIndex,
    pagination.pageSize,
    sorting
  ]);

  const deleteHandler = async () => {
    setIsLoading(true);
    const arnum = AuthVerify.getUser().arnum;
    const catalognum = searchParams.get("catalognum")
    await Axios.delete(`/corporate/catalog/${arnum}/${catalognum}/${selectedMatnum.current}`)
      .then(function (response) {
        setIsLoading(false);
        setIsError(false);
        setIsRefetching(false);
        if (response && response.data?.data) {
          setData(response.data?.data.items ?? []);
          setRowCount(response.data?.data.items.length);
        }
        else {
          toast.error(response.statusText ?? "Data fetching failed !", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 3000,
            transition: Slide,
          });
        }
      })
      .catch(function (error) {
        setIsLoading(false);
        // navigate('/');
      });
  }

  const columns = useMemo(
    () => [
      {
        accessorFn: (row) => row.budgetdesc,
        id: 'budgetdesc',
        // enableColumnFilter: false,
        // enableSorting: false,
        // enableColumnFilterModes: false,
        header: 'Description',
        enableEditing: false,
        size: 50,
        Cell: ({ cell, row }) => (
          // <a href="#">
          //   <img style={{ maxHeight: "60px" }} src={AuthVerify.getImageLink(cell.getValue())} alt="Product" />
          // </a>
          <Typography variant='h4'>{cell.getValue()}</Typography>
        ),
        filterFn: 'contains',
        columnFilterModeOptions: ['contains'],
      },
      {
        accessorKey: 'budgettype',
        header: "Type",
        Cell: ({ cell, row }) => (
            <Typography variant='h4'>{cell.getValue()}</Typography>
        ),
        size: 50,
        enableColumnFilterModes: true,
        filterFn: 'contains',
        columnFilterModeOptions: ['contains'],
      },
      {
        accessorKey: 'limitscheme',
        header: "Limit Scheme",
        Cell: ({ cell, row }) => (
          <Typography variant='h4'>{cell.getValue()}</Typography>
        ),
        size: 50,
        enableColumnFilterModes: true,
        filterFn: 'contains',
        columnFilterModeOptions: ['contains'],
      },
      {
        accessorKey: 'effdate',
        header: "Start Date",
        Cell: ({ cell, row }) => (
          <Typography variant='h4'>{cell.getValue().split('T')[0]}</Typography>
        ),
        size: 50,
        enableColumnFilterModes: true,
        filterFn: 'lessThan',
        columnFilterModeOptions: ['equals', 'lessThan', 'greaterThan'],
        Filter: ({ column, header, table }) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <input
              type="date"
              style={{margin:'6px 10px 3px 0',border:'1px solid #ddd',padding:'4px 0',color:'#777',fontSize:'13px'}}
              onChange={(newValue) => {
                column.setFilterValue(newValue.target.value);
              }}
              value={column.getFilterValue()}
            />
            <hr style={{margin:'0px',borderColor:'black'}}/>
            <p style={{color:'rgba(0, 0, 0, 0.6)',fontSize:'12px'}}>Filter Mode: Less Than</p>
          </LocalizationProvider>
          
        ),
      },
      {
        accessorKey: 'enddate',
        header: "End Date",
        Cell: ({ cell, row }) => (
          <Typography variant='h4'>{cell.getValue().split('T')[0]}</Typography>
        ),
        size: 50,
        enableColumnFilterModes: true,
        filterFn: 'lessThan',
        columnFilterModeOptions: ['equals', 'lessThan', 'greaterThan'],
        Filter: ({ column, header, table }) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <input
              type="date"
              style={{margin:'6px 10px 3px 0',border:'1px solid #ddd',padding:'4px 0',color:'#777',fontSize:'13px'}}
              onChange={(newValue) => {
                column.setFilterValue(newValue.target.value);
              }}
              value={column.getFilterValue()}
            />
            <hr style={{margin:'0px',borderColor:'black'}}/>
            <p style={{color:'rgba(0, 0, 0, 0.6)',fontSize:'12px'}}>Filter Mode: Less Than</p>
          </LocalizationProvider>
          
        ),
      },
      {
        accessorKey: 'ccname',
        header: "Cost Center",
        Cell: ({ cell, row }) => (
            <Typography variant='h4'>{cell.getValue()}</Typography>
        ),
        size: 50,
        enableColumnFilterModes: true,
        filterFn: 'contains',
        columnFilterModeOptions: ['contains'],
      }
      //end
    ],
    [],
  );
  
  return (
    <>
      <MaterialReactTable
        columns={columns}
        data={data}
        enableColumnActions={false}
        enableGlobalFilter={false}
        enableColumnFilterModes
        initialState={{
          showColumnFilters: false
        }}
        manualFiltering
        manualPagination
        manualSorting
        onColumnFilterFnsChange={setColumnFilterFns}
        onColumnFiltersChange={setColumnFilters}
        onGlobalFilterChange={setGlobalFilter}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        tableInstanceRef={tableInstanceRef}
        rowCount={rowCount}
        enableEditing
        state={{
          columnFilters,
          columnFilterFns,
          globalFilter,
          isLoading,
          pagination,
          showAlertBanner: isError,
          showProgressBars: isRefetching,
          sorting,
        }}
        positionActionsColumn="last"
        displayColumnDefOptions={{
          'mrt-row-actions': {
            header: 'Action', //change header text
          },
        }}
        renderRowActions={({ row, table }) => (
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            <Tooltip arrow placement="left" title="Details">
              <IconButton onClick={() => navigate(`/budget-details?budgetnum=${row.original.budgetnum}`)}>
                <List />
              </IconButton>
            </Tooltip>
            {editPermission &&
              <Tooltip arrow placement="right" title="Edit">
                <IconButton color="success" onClick={() => navigate(`/budget-details?budgetnum=${row.original.budgetnum}`)}>
                  <Edit />
                </IconButton>
              </Tooltip>}
          </Box>
        )}
        muiTableHeadCellProps={{
          sx: {
            // fontWeight: 'bold',
            fontSize: '15px',
          },
        }}
        muiTablePaginationProps={{
          sx: {
            fontWeight: 'bold',
            paddingTop: '12px',
            fontSize: '16px',
          },
        }}
        muiTableBodyCellEditTextFieldProps={({ cell, column, row, table }) => ({
          sx: {
            fontSize: '17px'
          },
        })}
        muiTableHeadCellFilterTextFieldProps={{
          sx: {
            fontWeight: 'bold',
            fontSize: '17px',
          },
        }}
      />

        <Modal show={dmShow} dialogClassName="modal-90w" animation={false} fade={false} style={{ opacity: 1 }} onHide={() => setDmShow(false)}>
        <Modal.Header closeButton={false}>
          <Modal.Title>Delete Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure about the delete ?
        </Modal.Body>
        <Modal.Footer>
          <button
            className="button btn-primary"
            title="Proceed to Checkout"
            type="button"
            onClick={() => handleDeleteRow()}
            style={{
              marginRight: "10px",
              padding: "10px"
            }}
          >
            <span>Confirm</span>
          </button>
          <button
            className="button btn-danger"
            title="Proceed to Checkout"
            type="button"
            onClick={() => setDmShow(false)}
            style={{
              marginRight: "10px",
              padding: "10px"
            }}
          >
            <span>Close</span>
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CustomTable;
